import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { resetPassword as resetPasswordRequest, usePasswordPolicy } from '@mygpt/app';
import {
  buildPasswordValidator,
  getEnabledPasswordRuleKeys,
  getPasswordPolicyErrorMessage,
  PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS,
} from '@mygpt/app/utils';

export default function RetrievePassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  // 密码策略:legacy(未配置/拉取失败)时保持现状校验,策略已配置时按规则预校验
  const { legacy: policyLegacy, rules: policyRules } = usePasswordPolicy();
  const enabledRuleKeys = !policyLegacy && policyRules ? getEnabledPasswordRuleKeys(policyRules) : [];

  useEffect(() => {
    // 解析 URL 中的查询参数
    const params = new URLSearchParams(location.search);
    const code = params.get('token');
    setResetToken(code);
    setMessage(null);

    if (!code) {
      setError(t('reset_password.reset_link_failed'));
      return;
    }

    setError(null);
  }, [location, t]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!password.trim()) {
      setError(t('adminConsole.employees.passwordRequired'));
      setMessage(null);
      return;
    }
    if (policyLegacy || !policyRules) {
      // legacy 模式:保持现状(长度≥8 + 4 类字符取 3)
      if (password.trim().length < 8) {
        setError(t('login_password_min_length'));
        setMessage(null);
        return;
      }
      {
        const pwd = password.trim();
        let categories = 0;
        if (/[a-z]/.test(pwd)) categories++;
        if (/[A-Z]/.test(pwd)) categories++;
        if (/[0-9]/.test(pwd)) categories++;
        if (/[^a-zA-Z0-9]/.test(pwd)) categories++;
        if (categories < 3) {
          setError(t('reset_password.password_too_weak'));
          setMessage(null);
          return;
        }
      }
    } else {
      // 策略模式:按已配置规则预校验(min_length_8 / require_case_digit / require_special)
      const violations = buildPasswordValidator(policyRules)(password.trim());
      if (violations.length) {
        setError(violations.map((v) => t(PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS[v])).join('; '));
        setMessage(null);
        return;
      }
    }
    if (password !== confirmPassword) {
      setError(t('adminConsole.employees.passwordsDoNotMatch'));
      setMessage(null);
      return;
    }
    const currentToken = resetToken;
    if (!currentToken) {
      setError(t('reset_password.reset_link_failed'));
      setMessage(null);
      return;
    }
    setIsSubmitting(true);
    try {
      await resetPasswordRequest({
        resetToken: currentToken,
        newPassword: password.trim(),
      });
      setMessage(t('reset_password.success'));
      setError(null);
      navigate('/auth/login');
    } catch (err) {
      const apiError = (typeof err === 'object' && err !== null ? err : {}) as {
        statusCode?: number;
        errorMsg?: string;
      };
      // 新错误码分支:PASSWORD_POLICY_VIOLATION / PASSWORD_REUSED(detail.code 判别)
      const policyErrorMessage = getPasswordPolicyErrorMessage(err, t);
      // 401 = reset token 无效或已过期，展示明确的引导文案（i18n）
      const errorMessage =
        policyErrorMessage ??
        (apiError.statusCode === 401
          ? t('reset_password.invalid_or_expired_token')
          : typeof apiError.errorMsg === 'string' && apiError.errorMsg
          ? apiError.errorMsg
          : t('reset_password.reset_link_failed'));
      setError(errorMessage);
      setMessage(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-gray-50 py-12 px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          {t('reset_password.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">{t('reset_password.description')}</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow rounded-xl border border-gray-200 sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <p className="ml-3 text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}
            {message && (
              <div className="rounded-md bg-green-50 p-4">
                <p className="text-sm text-green-700">{message}</p>
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                {t('reset_password.new_password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full rounded-xl border border-gray-300 px-4 py-3
                  text-gray-900 placeholder-gray-400 bg-gray-50
                  hover:border-gray-400 focus:border-primary-500
                  focus:ring-1 focus:ring-primary-500 focus:bg-white
                  transition-all duration-200"
                placeholder={t('reset_password.enter_new_password')}
              />
              <p className="mt-1 text-xs text-gray-500">
                {policyLegacy || !policyRules
                  ? t('reset_password.password_requirements')
                  : enabledRuleKeys.length > 0
                  ? `${t('password_policy.hint_title')} ${enabledRuleKeys
                      .map((key) => t(PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS[key]))
                      .join('; ')}`
                  : null}
              </p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                {t('reset_password.confirm_password')}
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="block w-full rounded-xl border border-gray-300 px-4 py-3
                  text-gray-900 placeholder-gray-400 bg-gray-50
                  hover:border-gray-400 focus:border-primary-500
                  focus:ring-1 focus:ring-primary-500 focus:bg-white
                  transition-all duration-200"
                placeholder={t('reset_password.confirm_new_password')}
              />
            </div>

            <div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="w-full flex justify-center items-center px-4 py-3 
                  bg-gray-900 text-white rounded-lg hover:bg-gray-800
                  shadow-sm font-medium transition-all duration-200"
                disabled={isSubmitting || !resetToken}
              >
                <span className="text-base font-medium">{t('reset_password.reset_button')}</span>
              </motion.button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
